import React from "react";
import { useLocation } from 'react-router-dom';
import { API_URL } from "../config/constants";
import { clearAuthToken, getAuthToken } from "./authToken";

export const authHeader = () => {
    const token = getAuthToken();
    if (token) {
        return { Authorization: 'Bearer ' + token };
    }
    return {};
};

/**
 * Normalizes fetch responses. On failure: throws Error with .status / .body.
 * Accepts either a Response or a Promise<Response>; translates raw network failures.
 */
export const handleResponse = async (response, onError) => {
    let res;
    try {
        res = await response;
    } catch (networkErr) {
        const friendly =
            "Couldn't reach the ATO server. Check your internet connection or any ad/privacy blockers, then try again.";
        const err = new Error(friendly);
        err.status = 0;
        err.cause = networkErr;
        console.error('[API network error]', networkErr && networkErr.message ? networkErr.message : networkErr);
        throw err;
    }

    const text = await res.text();
    let data = null;
    if (text) {
        try {
            data = JSON.parse(text);
        } catch {
            data = { message: text.slice(0, 800) };
        }
    }

    if (!res.ok) {
        if (res.status === 401 && onError) {
            onError();
        }

        const message =
            (data && (data.message || (typeof data.error === 'string' ? data.error : null))) ||
            res.statusText ||
            'Request failed';

        const err = new Error(message);
        err.status = res.status;
        err.body = data;
        console.error('[API error]', res.url || '(no url)', res.status, message, data ?? text);

        // Expired or missing session on protected routes — clear stale token and re-login.
        const requestUrl = res.url || '';
        const isAuthRoute = /\/auth\/(login|register|reset)/i.test(requestUrl);
        if (res.status === 401 && !isAuthRoute && typeof window !== 'undefined') {
            clearAuthToken();
            if (!window.location.pathname.startsWith('/login')) {
                window.location.replace('/login?session=expired');
            }
        }

        throw err;
    }

    return data;
};

export const getResolvedPath = (file) => {
    try {
        if (!file || !file.path) return '';
        if (/^https?:\/\//i.test(file.path)) return file.path;
        const pathSeg = encodeURIComponent(String(file.path));
        if (file.name) {
            return `${API_URL}/files/${pathSeg}/${encodeURIComponent(String(file.name))}`;
        }
        return `${API_URL}/files/${pathSeg}`;
    } catch (err) {
        console.log(err);
        return '';
    }
};

export const useQuery = () => {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
};
